import { useEffect, useState } from 'react'
import { doc, onSnapshot } from 'firebase/firestore'
import { firestore } from '../firebase'
import { useAuth } from '../auth/AuthProvider'

/**
 * Diagnostic section that dumps the user's raw Firestore documents so
 * they can confirm what the desktop actually wrote. We subscribe to
 * four docs: settings/frontend, library/movies, library/series,
 * library/watchlists. Everything renders in collapsible <details>
 * blocks with JSON.stringify + a tmdbId count, so even very large
 * library docs are navigable on a phone.
 */
export function FirestoreDebug() {
  const { user } = useAuth()
  const [movies, setMovies] = useState<Record<string, unknown> | null>(null)
  const [series, setSeries] = useState<Record<string, unknown> | null>(null)
  const [watchlists, setWatchlists] = useState<Record<string, unknown> | null>(null)
  const [settings, setSettings] = useState<Record<string, unknown> | null>(null)
  const [moviesExists, setMoviesExists] = useState<boolean | null>(null)
  const [seriesExists, setSeriesExists] = useState<boolean | null>(null)
  const [watchlistsExists, setWatchlistsExists] = useState<boolean | null>(null)
  const [settingsExists, setSettingsExists] = useState<boolean | null>(null)

  useEffect(() => {
    if (!user) return
    const unsubs: (() => void)[] = []
    const watch = <T,>(
      path: string,
      setData: (v: Record<string, T> | null) => void,
      setExists: (v: boolean) => void
    ) => {
      const ref = doc(firestore, path)
      const unsub = onSnapshot(
        ref,
        (snap) => {
          setExists(snap.exists())
          setData(snap.exists() ? (snap.data() as Record<string, T>) : null)
        },
        (err) => {
          console.error('[FirestoreDebug]', path, err)
          setExists(false)
        }
      )
      unsubs.push(unsub)
    }
    watch(`users/${user.uid}/settings/frontend`, setSettings, setSettingsExists)
    watch(`users/${user.uid}/library/movies`, setMovies, setMoviesExists)
    watch(`users/${user.uid}/library/series`, setSeries, setSeriesExists)
    watch(`users/${user.uid}/library/watchlists`, setWatchlists, setWatchlistsExists)
    return () => unsubs.forEach((u) => u())
  }, [user])

  const moviesWithRating = countWithRating(movies)
  const seriesWithRating = countWithRating(series)

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-text-tertiary">
        Live dump of the four Firestore docs the PWA reads. Use this to verify
        what the desktop actually synced. User ratings that should be visible
        on the cards but aren't will show up here first if they're in Firebase
        at all.
      </p>

      <Stat label="users/{uid}/library/movies" exists={moviesExists}>
        {movies && (
          <>
            <p className="mb-1 text-[10px] text-text-tertiary">
              {Object.keys(movies).length} entries · {moviesWithRating} with
              userRating set
            </p>
            <Json data={movies} />
          </>
        )}
      </Stat>

      <Stat label="users/{uid}/library/series" exists={seriesExists}>
        {series && (
          <>
            <p className="mb-1 text-[10px] text-text-tertiary">
              {Object.keys(series).length} entries · {seriesWithRating} with
              userRating set
            </p>
            <Json data={series} />
          </>
        )}
      </Stat>

      <Stat label="users/{uid}/library/watchlists" exists={watchlistsExists}>
        {watchlists && (
          <>
            <p className="mb-1 text-[10px] text-text-tertiary">
              {Object.keys(watchlists).length} watchlists
            </p>
            <Json data={watchlists} />
          </>
        )}
      </Stat>

      <Stat label="users/{uid}/settings/frontend" exists={settingsExists}>
        {settings && <Json data={redactSettings(settings)} />}
      </Stat>

      <details className="rounded-md border border-border-primary bg-bg-secondary p-3">
        <summary className="cursor-pointer text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
          Also check Firebase Console
        </summary>
        <div className="mt-2 space-y-1 text-[11px] text-text-secondary">
          <p>
            Open{' '}
            <span className="font-mono text-accent-primary">
              console.firebase.google.com
            </span>{' '}
            → your Govu project → Firestore Database → Data tab.
          </p>
          <p>
            Navigate to{' '}
            <span className="font-mono text-text-primary">
              users / {user?.uid || '{uid}'} / library / movies
            </span>{' '}
            and expand any tmdbId field.
          </p>
          <p>
            If the <span className="font-mono text-text-primary">userRating</span>{' '}
            field is missing or null, the desktop didn't sync it. If it's
            present in the console but not showing up in the PWA card, there's
            a reader bug — send me the tmdbId + value and I'll trace it.
          </p>
        </div>
      </details>
    </div>
  )
}

function Stat({
  label,
  exists,
  children,
}: {
  label: string
  exists: boolean | null
  children: React.ReactNode
}) {
  return (
    <details className="rounded-md border border-border-primary bg-bg-secondary p-3">
      <summary className="cursor-pointer text-[11px] font-semibold text-text-primary">
        <span className="font-mono text-[10px] text-text-tertiary">{label}</span>
        <span className="ml-2 text-[10px]">
          {exists === null ? (
            <span className="text-text-tertiary">…</span>
          ) : exists ? (
            <span className="text-accent-primary">exists</span>
          ) : (
            <span className="text-accent-danger">missing</span>
          )}
        </span>
      </summary>
      <div className="mt-3">{children}</div>
    </details>
  )
}

function Json({ data }: { data: unknown }) {
  return (
    <pre className="max-h-64 overflow-auto rounded bg-bg-primary p-2 text-[10px] text-text-secondary">
      {JSON.stringify(data, null, 2)}
    </pre>
  )
}

function countWithRating(obj: Record<string, unknown> | null): number {
  if (!obj) return 0
  let n = 0
  for (const entry of Object.values(obj)) {
    if (entry && typeof entry === 'object') {
      const rating = (entry as { userRating?: unknown }).userRating
      if (typeof rating === 'number' && rating > 0) n++
    }
  }
  return n
}

function redactSettings(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...obj }
  for (const key of Object.keys(out)) {
    if (/key|token|secret/i.test(key) && typeof out[key] === 'string') {
      const s = out[key] as string
      out[key] = s ? `${s.slice(0, 4)}…${s.slice(-4)} (${s.length} chars)` : null
    }
  }
  return out
}
